import { useEffect, useRef, useState } from 'react';
import { useQuery, useBalloonEventsMetadata, useApi, useTheme, type FilterType } from 'react-ui';
import { apiFetch, BeatAPISavedSearchRepository, pendingAutoFit } from 'ts-core';
import { useMapBox } from './BalloonEventsMapBox';
import { useApplyTagFilter } from '../hooks/useApplyTagFilter';
import { buildQueryFromSavedSearch } from '../utils/buildQueryFromSavedSearch';
import mapboxgl from 'mapbox-gl';
import dayjs from 'dayjs';

// Re-exported so callers that still import from this file keep working.
export { buildQueryFromSavedSearch };

interface InitialFilterApplierProps {
    filter: { type: FilterType; value: string };
}

interface DatePreset {
    from: Date;
    to: Date;
}

function getDatePreset(preset: string): DatePreset | null {
    const now = dayjs();
    const presetLower = preset.toLowerCase();
    
    switch (presetLower) {
        case 'today':
            return {
                from: now.startOf('day').toDate(),
                to: now.endOf('day').toDate()
            };
        case 'tomorrow':
            return {
                from: now.add(1, 'day').startOf('day').toDate(),
                to: now.add(1, 'day').endOf('day').toDate()
            };
        case 'this-week':
            return {
                from: now.startOf('week').toDate(),
                to: now.endOf('week').toDate()
            };
        case 'this-weekend': {
            const saturday = now.day(6).startOf('day');
            const sunday = now.day(0).add(1, 'week').endOf('day');
            return {
                from: saturday.toDate(),
                to: sunday.toDate()
            };
        }
        case 'this-month':
            return {
                from: now.startOf('month').toDate(),
                to: now.endOf('month').toDate()
            };
        case 'next-week':
            return {
                from: now.add(1, 'week').startOf('week').toDate(),
                to: now.add(1, 'week').endOf('week').toDate()
            };
        case 'next-month':
            return {
                from: now.add(1, 'month').startOf('month').toDate(),
                to: now.add(1, 'month').endOf('month').toDate()
            };
        default:
            return null;
    }
}

/**
 * Component that applies an initial filter inside the QueryProvider context.
 * This is used when filter info is passed from outside the map (e.g., from URL in site app).
 */
export function InitialFilterApplier({ filter }: InitialFilterApplierProps) {
    const { addQuery, has } = useQuery();
    const { metadata } = useBalloonEventsMetadata();
    const { apiEndpoints, apiKey } = useApi();
    const applyTag = useApplyTagFilter();
    const { primaryColor } = useTheme();
    const mapBoxInfo = useMapBox();
    const map = mapBoxInfo?.map;
    const appliedRef = useRef<string | null>(null);
    const centerMarkerRef = useRef<mapboxgl.Marker | null>(null);
    
    const { type, value } = filter;
    const valueLower = value.toLowerCase();

    useEffect(() => {
        const filterKey = `${type}:${value}`;
        if (appliedRef.current === filterKey || !value) return;
        
        const applyFilter = async () => {
            switch (type) {
                case 'tags': {
                    // Shared hook: tag layer + tag-bound saved-search merge
                    // (forceMapClustering, amount, ...). Matches SearchBar and
                    // banner-click via the same code path.
                    await applyTag(valueLower, { skipIfExists: true, alterHistory: false });
                    appliedRef.current = filterKey;
                    break;
                }
                
                case 'categories': {
                    if (!metadata) return;
                    const category = metadata.getCategoryBySlug(valueLower);
                    if (category) {
                        const query = { id: category.id, categories: [category.id] };
                        if (!has(category.id)) {
                            // Arm one-shot map auto-fit for deep-linked category routes.
                            pendingAutoFit.arm();
                            addQuery(query, false);
                        }
                        appliedRef.current = filterKey;
                    } else {
                        console.warn('[InitialFilterApplier] Category not found:', value);
                    }
                    break;
                }
                
                case 'places': {
                    if (!apiEndpoints?.region || !apiKey || !map) return;
                    try {
                        const response = await apiFetch(
                            `${apiEndpoints.region}/${encodeURIComponent(value)}/coordinates`,
                            {
                                method: 'GET',
                                apiKey,
                            }
                        );
                        
                        if (!response.ok) {
                            console.warn('[InitialFilterApplier] Region not found:', value);
                            return;
                        }
                        
                        const coords = await response.json();
                        
                        if (centerMarkerRef.current) {
                            centerMarkerRef.current.remove();
                        }
                        
                        const marker = new mapboxgl.Marker({
                            color: primaryColor,
                            scale: 1.5,
                        })
                            .setLngLat(coords)
                            .addTo(map);
                        centerMarkerRef.current = marker;
                        
                        map.flyTo({
                            center: coords,
                            zoom: 11,
                        });
                        
                        appliedRef.current = filterKey;
                    } catch (error) {
                        console.error('[InitialFilterApplier] Failed to fetch region coordinates:', error);
                    }
                    break;
                }
                
                case 'sources': {
                    const queryId = `source:${valueLower}`;
                    if (!has(queryId)) {
                        addQuery({ id: queryId, sources: [valueLower] }, false);
                    }
                    appliedRef.current = filterKey;
                    break;
                }
                
                case 'dates': {
                    const datePreset = getDatePreset(value);
                    if (datePreset) {
                        const queryId = `dates:${valueLower}`;
                        if (!has(queryId)) {
                            addQuery({ id: queryId, dates: [datePreset.from, datePreset.to] }, false);
                        }
                        appliedRef.current = filterKey;
                    } else {
                        console.warn('[InitialFilterApplier] Invalid date preset:', value);
                    }
                    break;
                }

                case 'search': {
                    if (!apiEndpoints?.savedSearches || !apiKey) return;
                    try {
                        const repo = new BeatAPISavedSearchRepository(apiEndpoints.savedSearches, apiKey);
                        const saved = await repo.getBySlug(value);
                        if (!saved) {
                            console.warn('[InitialFilterApplier] Saved search not found:', value);
                            return;
                        }
                        const query = buildQueryFromSavedSearch(saved, metadata);
                        const queryId = `saved-search:${saved.slug}`;
                        if (!has(queryId)) {
                            addQuery({ ...query, id: queryId }, false);
                        }
                        appliedRef.current = filterKey;
                    } catch (error) {
                        console.error('[InitialFilterApplier] Failed to load saved search:', error);
                    }
                    break;
                }
            }
        };

        applyFilter();
    }, [type, value, valueLower, metadata, apiEndpoints, apiKey, addQuery, has, map, primaryColor]);

    return null;
}

export default InitialFilterApplier;
